/**
 * lib/glReport.ts
 * ------------------------------------------------------------------
 * Reading the ledger: trial balance, account ledgers, running balances.
 *
 * PURE. No Firestore, no clock, no credentials — journals in, figures out —
 * so the arithmetic a school will sign its accounts on can be exercised
 * exhaustively offline.
 *
 * NOTHING HERE IS STORED. Rule 3 of the ledger: a balance is a reduce over
 * journal lines, computed on demand, never written onto an account. A stored
 * balance is a balance that drifts from the entries behind it, and the drift
 * is silent — the account says one thing, its own history says another, and
 * there is no way to tell which is right.
 *
 * A REVERSED JOURNAL IS STILL IN THE LEDGER. It happened. Its reversal also
 * happened, and the two net to nil on every account they touch. Filtering
 * either out would make the trial balance disagree with the register a
 * bursar is reading, which is worse than showing both.
 *
 * SIGN CONVENTION. Every figure here is carried as (debits − credits), which
 * is positive for assets and expenses and negative for liabilities, equity
 * and income. Presentation flips the sign for the credit-natured accounts at
 * the very edge — `signedBalance` — so the arithmetic in the middle only
 * ever adds. Mixing presentation into the totals is how a trial balance
 * stops balancing for reasons nobody can find.
 * ------------------------------------------------------------------ */
import { normalBalance, type AccountType, type GlAccount, type GlJournal } from '@/lib/gl';

const round2 = (n: number) => Math.round((Number(n) || 0) * 100) / 100;

/* ========================= account balances ========================= */

export interface AccountMovement {
  /** Debits minus credits. Positive is a debit balance. */
  net: number;
  totalDr: number;
  totalCr: number;
  /** How many journal lines touched this account. */
  entries: number;
}

const emptyMovement = (): AccountMovement => ({ net: 0, totalDr: 0, totalCr: 0, entries: 0 });

export interface DateRange {
  /** Inclusive ISO date, or omitted for "from the beginning". */
  from?: string;
  /** Inclusive ISO date, or omitted for "to the end". */
  to?: string;
}

/** Does this journal fall inside the range? Both ends inclusive. */
export function inRange(date: string, range: DateRange = {}): boolean {
  const d = String(date ?? '').slice(0, 10);
  if (!d) return false;
  if (range.from && d < range.from) return false;
  if (range.to && d > range.to) return false;
  return true;
}

/**
 * Movement per account code across the journals given.
 *
 * Accounts with no entries are absent rather than zero — a trial balance
 * listing every account in the chart at nil is a page of noise that hides
 * the handful that moved.
 */
export function movementsByAccount(
  journals: GlJournal[],
  range: DateRange = {},
): Map<string, AccountMovement> {
  const out = new Map<string, AccountMovement>();
  for (const j of journals) {
    if (!inRange(j.date, range)) continue;
    for (const l of j.lines ?? []) {
      if (!l.accountCode) continue;
      const m = out.get(l.accountCode) ?? emptyMovement();
      m.totalDr = round2(m.totalDr + (Number(l.dr) || 0));
      m.totalCr = round2(m.totalCr + (Number(l.cr) || 0));
      m.net = round2(m.totalDr - m.totalCr);
      m.entries += 1;
      out.set(l.accountCode, m);
    }
  }
  return out;
}

/**
 * The figure to PRINT for an account, positive when it sits on its normal
 * side.
 *
 * A liability of 5,000 is carried internally as −5,000 (credits exceed
 * debits) and shown as 5,000, because "Accounts Payable −5,000" reads as a
 * debit balance to anyone who knows what they are looking at. A liability
 * that genuinely IS in debit comes out negative, which is exactly the
 * anomaly a bursar wants to see.
 */
export function signedBalance(net: number, type: AccountType): number {
  return normalBalance(type) === 'dr' ? round2(net) : round2(-net);
}

/* =========================== trial balance =========================== */

export interface TrialBalanceRow {
  code: string;
  name: string;
  type: AccountType;
  parentCode?: string;
  /** Debits minus credits — the raw figure. */
  net: number;
  /** What goes in the Debit column, or 0. */
  dr: number;
  /** What goes in the Credit column, or 0. */
  cr: number;
  entries: number;
}

export interface TrialBalance {
  rows: TrialBalanceRow[];
  totalDr: number;
  totalCr: number;
  /** To the thebe. If this is ever false the ledger is broken, not the report. */
  balanced: boolean;
  /** totalDr − totalCr. Zero when balanced; shown when it is not. */
  difference: number;
}

/**
 * Build a trial balance.
 *
 * A trial balance that does not balance is not a rounding curiosity: every
 * journal is asserted balanced before it is written, so an out-of-balance
 * total means either a journal was written around that check or lines have
 * been lost. Either is worth stopping for, which is why `balanced` is
 * carried on the result rather than left for the caller to work out.
 *
 * Only POSTABLE accounts appear. A header exists to subtotal; giving it a
 * balance of its own would double-count everything beneath it.
 */
export function buildTrialBalance(
  accounts: GlAccount[],
  journals: GlJournal[],
  range: DateRange = {},
): TrialBalance {
  const moves = movementsByAccount(journals, range);
  const byCode = new Map(accounts.map((a) => [a.code, a]));

  const rows: TrialBalanceRow[] = [];
  for (const [code, m] of moves) {
    const a = byCode.get(code);
    rows.push({
      code,
      // An account that has been posted to and then deleted from the chart
      // must still appear, or the trial balance silently stops balancing.
      name: a?.name ?? '(account no longer in the chart)',
      type: a?.type ?? 'asset',
      parentCode: a?.parentCode,
      net: m.net,
      dr: m.net > 0 ? m.net : 0,
      cr: m.net < 0 ? round2(-m.net) : 0,
      entries: m.entries,
    });
  }

  rows.sort((x, y) => x.code.localeCompare(y.code));

  const totalDr = round2(rows.reduce((a, r) => a + r.dr, 0));
  const totalCr = round2(rows.reduce((a, r) => a + r.cr, 0));
  const difference = round2(totalDr - totalCr);

  return { rows, totalDr, totalCr, balanced: difference === 0, difference };
}

/* ========================== account ledger ========================== */

export interface LedgerEntry {
  journalId: string;
  journalNo: string;
  date: string;
  narration: string;
  /** The line's own narration, when it carries one. */
  lineNarration?: string;
  dr: number;
  cr: number;
  /** Balance after this entry, debits-minus-credits. */
  balance: number;
  sourceType: string;
  reversed: boolean;
}

export interface AccountLedger {
  code: string;
  name: string;
  type: AccountType;
  /** Balance before the first entry in range. */
  opening: number;
  entries: LedgerEntry[];
  /** Balance after the last entry in range. */
  closing: number;
  totalDr: number;
  totalCr: number;
}

/**
 * Every movement on one account, in date order, with a running balance.
 *
 * THE OPENING BALANCE IS EVERYTHING BEFORE `from`, not zero. A ledger that
 * starts at nil in the middle of a year is a ledger whose closing figure
 * disagrees with the trial balance, and the bursar is left to guess which
 * one lied.
 *
 * Ordering is by DATE then JOURNAL NUMBER. Two journals on the same day need
 * a stable order or the running balance changes between two renders of the
 * same data, and a running balance that moves is a running balance nobody
 * checks against.
 */
export function buildAccountLedger(
  code: string,
  accounts: GlAccount[],
  journals: GlJournal[],
  range: DateRange = {},
): AccountLedger {
  const account = accounts.find((a) => a.code === code);

  const before = range.from
    ? movementsByAccount(journals, { to: priorDay(range.from) }).get(code)
    : undefined;
  const opening = round2(before?.net ?? 0);

  const rows: LedgerEntry[] = [];
  for (const j of journals) {
    if (!inRange(j.date, range)) continue;
    for (const l of j.lines ?? []) {
      if (l.accountCode !== code) continue;
      rows.push({
        journalId: j.id,
        journalNo: j.journalNo,
        date: String(j.date).slice(0, 10),
        narration: j.narration ?? '',
        ...(l.narration ? { lineNarration: l.narration } : {}),
        dr: round2(Number(l.dr) || 0),
        cr: round2(Number(l.cr) || 0),
        balance: 0,
        sourceType: j.source?.type ?? 'manual',
        reversed: !!j.reversedBy || !!j.reverses,
      });
    }
  }

  rows.sort((a, b) => a.date.localeCompare(b.date) || a.journalNo.localeCompare(b.journalNo));

  let running = opening;
  for (const r of rows) {
    running = round2(running + r.dr - r.cr);
    r.balance = running;
  }

  return {
    code,
    name: account?.name ?? '(account no longer in the chart)',
    type: account?.type ?? 'asset',
    opening,
    entries: rows,
    closing: running,
    totalDr: round2(rows.reduce((a, r) => a + r.dr, 0)),
    totalCr: round2(rows.reduce((a, r) => a + r.cr, 0)),
  };
}

/** The day before an ISO date — the cut for an opening balance. */
export function priorDay(iso: string): string {
  const d = new Date(`${String(iso).slice(0, 10)}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return iso;
  d.setUTCDate(d.getUTCDate() - 1);
  return d.toISOString().slice(0, 10);
}

/* ===================== grouped by statement ===================== */

export interface TypeGroup {
  type: AccountType;
  label: string;
  rows: TrialBalanceRow[];
  /** Total on the type's NORMAL side — what a reader expects to see. */
  total: number;
}

const TYPE_LABEL: Record<AccountType, string> = {
  asset: 'Assets',
  liability: 'Liabilities',
  equity: 'Equity',
  income: 'Income',
  expense: 'Expenditure',
};

const TYPE_ORDER: AccountType[] = ['asset', 'liability', 'equity', 'income', 'expense'];

/**
 * The trial balance arranged the way a set of accounts reads.
 *
 * Each total is on the type's own normal side, so Income reads as a positive
 * number rather than as the negative it is carried as internally. An empty
 * type is dropped: a heading with nothing under it suggests something failed
 * to load.
 */
export function groupByType(tb: TrialBalance): TypeGroup[] {
  return TYPE_ORDER.map((type) => {
    const rows = tb.rows.filter((r) => r.type === type);
    return {
      type,
      label: TYPE_LABEL[type],
      rows,
      total: round2(rows.reduce((a, r) => a + signedBalance(r.net, type), 0)),
    };
  }).filter((g) => g.rows.length > 0);
}

/**
 * Surplus or deficit for the period: income less expenditure.
 *
 * Both are returned on their normal sides, so this is a subtraction a reader
 * can follow rather than a sum of two figures with opposite signs.
 */
export function surplusForPeriod(tb: TrialBalance): {
  income: number;
  expenditure: number;
  surplus: number;
} {
  const income = round2(
    tb.rows.filter((r) => r.type === 'income').reduce((a, r) => a + signedBalance(r.net, 'income'), 0),
  );
  const expenditure = round2(
    tb.rows.filter((r) => r.type === 'expense').reduce((a, r) => a + signedBalance(r.net, 'expense'), 0),
  );
  return { income, expenditure, surplus: round2(income - expenditure) };
}
